import React from "react";
import { Clock, CalendarDays, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface CountdownProps {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface ExhibitionCountdownProps {
  eventName: string;
  eventDescription?: string | null;
  startsAt: string;
  countdown: CountdownProps | null;
  isLive: boolean;
}

const ExhibitionCountdown: React.FC<ExhibitionCountdownProps> = ({
  eventName,
  eventDescription,
  startsAt,
  countdown,
  isLive,
}) => {
  if (isLive) {
    return (
      <div className="mx-4 mt-4 p-4 rounded-2xl bg-gradient-to-r from-green-500/20 via-emerald-500/20 to-teal-500/20 backdrop-blur-xl border border-green-400/30">
        <div className="flex items-center gap-3 mb-2">
          <div className="relative">
            <span className="flex h-4 w-4">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-4 w-4 bg-green-500" />
            </span>
          </div>
          <h3 className="text-lg font-bold text-green-700 dark:text-green-300">
            {eventName} is LIVE!
          </h3>
        </div>
        {eventDescription && (
          <p className="text-sm text-green-600/80 dark:text-green-400/80">
            {eventDescription}
          </p>
        )}
      </div>
    );
  }

  if (!countdown) return null;

  const TimeUnit = ({ value, label }: { value: number; label: string }) => (
    <div className="flex flex-col items-center">
      <div className="relative w-14 h-14 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center shadow-lg">
        <span className="text-2xl font-bold text-white">
          {value.toString().padStart(2, "0")}
        </span>
        <div className="absolute inset-0 rounded-xl bg-white/10 animate-pulse" />
      </div>
      <span className="text-xs text-muted-foreground mt-1 font-medium uppercase">
        {label}
      </span>
    </div>
  );

  const Separator = () => (
    <div className="flex flex-col items-center justify-center gap-1 -mt-5">
      <div className="w-1.5 h-1.5 rounded-full bg-pink-400" />
      <div className="w-1.5 h-1.5 rounded-full bg-pink-400" />
    </div>
  );

  return (
    <div className="mx-4 mt-4 p-4 rounded-2xl bg-gradient-to-br from-pink-500/10 via-rose-500/10 to-amber-500/10 backdrop-blur-xl border border-pink-300/30">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-5 h-5 text-amber-500" />
        <h3 className="text-lg font-bold text-foreground">Upcoming Event</h3>
      </div>

      {/* Event name */}
      <p className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-rose-500 mb-1">
        {eventName}
      </p>
      
      {eventDescription && (
        <p className="text-sm text-muted-foreground mb-3">{eventDescription}</p>
      )}

      {/* Date */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
        <CalendarDays className="w-4 h-4" />
        <span>{format(new Date(startsAt), "EEEE, MMMM d 'at' h:mm a")}</span>
      </div>

      {/* Countdown */}
      <div className="flex items-center justify-center gap-3">
        {countdown.days > 0 && (
          <>
            <TimeUnit value={countdown.days} label="Days" />
            <Separator />
          </>
        )}
        <TimeUnit value={countdown.hours} label="Hours" />
        <Separator />
        <TimeUnit value={countdown.minutes} label="Mins" />
        <Separator />
        <TimeUnit value={countdown.seconds} label="Secs" />
      </div>

      {/* Remind me */}
      <button className="w-full mt-4 py-2 rounded-full bg-pink-500/20 text-pink-600 dark:text-pink-400 font-medium text-sm flex items-center justify-center gap-2 hover:bg-pink-500/30 transition-colors">
        <Clock className="w-4 h-4" />
        Set Reminder
      </button>
    </div>
  );
};

export default ExhibitionCountdown;
